import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, Check } from 'lucide-react'
import api from '../../services/api'

/**
 * ServiceoverzichtSectie — Afwerkingspunten beheer voor installatiejobs.
 * Toont open/in behandeling/voltooid punten met toggle en toevoeg-formulier.
 */
export default function ServiceoverzichtSectie({ projectId }) {
  const [toonNieuwPunt, setToonNieuwPunt] = useState(false)
  const [nieuwOmschrijving, setNieuwOmschrijving] = useState('')
  const [nieuwPrioriteit, setNieuwPrioriteit] = useState('Normaal')

  const { data: overzicht, isLoading, refetch } = useQuery({
    queryKey: ['serviceoverzicht-mobile', projectId],
    queryFn: () => api.get(`/serviceoverzicht/project/${projectId}`).then(r => r.data),
    enabled: !!projectId,
  })

  const updatePuntStatus = async (punt, nieuweStatus) => {
    try {
      await api.put(`/serviceoverzicht/afwerkingspunten/${punt.id}`, {
        status: nieuweStatus,
      })
      refetch()
    } catch { /* silent */ }
  }

  const voegPuntToe = async () => {
    if (!nieuwOmschrijving.trim() || !overzicht?.id) return
    try {
      await api.post(`/serviceoverzicht/${overzicht.id}/afwerkingspunten`, {
        omschrijving: nieuwOmschrijving.trim(),
        prioriteit: nieuwPrioriteit,
        bestelling_vereist: false,
      })
      setNieuwOmschrijving('')
      setToonNieuwPunt(false)
      refetch()
    } catch { /* silent */ }
  }

  return (
    <div className="bg-white rounded-lg shadow border border-line dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900">Afwerkingspunten</h2>
        <button
          onClick={() => setToonNieuwPunt(v => !v)}
          className="text-sm text-blue-600 font-medium flex items-center gap-1"
        >
          <Plus size={16} /> Toevoegen
        </button>
      </div>

      {/* Nieuw punt formulier */}
      {toonNieuwPunt && (
        <div className="border border-gray-200 rounded-lg p-4 mb-3">
          <input
            value={nieuwOmschrijving}
            onChange={e => setNieuwOmschrijving(e.target.value)}
            placeholder="Omschrijving afwerkingspunt"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2"
          />
          <select
            value={nieuwPrioriteit}
            onChange={e => setNieuwPrioriteit(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3"
          >
            <option value="Laag">Laag</option>
            <option value="Normaal">Normaal</option>
            <option value="Hoog">Hoog</option>
          </select>
          <div className="flex gap-2">
            <button
              onClick={() => setToonNieuwPunt(false)}
              className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600"
            >
              Annuleer
            </button>
            <button
              onClick={voegPuntToe}
              className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium"
            >
              Toevoegen
            </button>
          </div>
        </div>
      )}

      {isLoading && <p className="text-sm text-gray-400">Laden&hellip;</p>}

      {/* Puntenlijst */}
      {overzicht && (
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          {overzicht.afwerkingspunten?.length === 0 && (
            <p className="text-sm text-gray-400 italic px-4 py-3">
              Geen afwerkingspunten.
            </p>
          )}
          {overzicht.afwerkingspunten?.map((punt) => (
            <div
              key={punt.id}
              className={`px-4 py-3 border-b border-gray-100 last:border-0 ${
                punt.status === 'Voltooid' ? 'opacity-50' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Status knop */}
                <button
                  onClick={() => updatePuntStatus(
                    punt,
                    punt.status === 'Voltooid' ? 'Open' : 'Voltooid'
                  )}
                  className={`mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    punt.status === 'Voltooid'
                      ? 'bg-green-500 border-green-500 text-white'
                      : punt.status === 'In_Behandeling'
                      ? 'border-orange-400 bg-orange-50'
                      : 'border-gray-300'
                  }`}
                >
                  {punt.status === 'Voltooid' && <Check size={12} />}
                  {punt.status === 'In_Behandeling' && (
                    <div className="w-2 h-2 rounded-full bg-orange-400" />
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    punt.status === 'Voltooid'
                      ? 'line-through text-gray-400'
                      : 'text-gray-900'
                  }`}>
                    {punt.omschrijving}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <PrioriteitBadge prioriteit={punt.prioriteit} />
                    {punt.bestelling_vereist && (
                      <span className="text-xs text-orange-600">
                        {'\uD83D\uDCE6'} bestelling vereist
                      </span>
                    )}
                  </div>
                </div>

                {/* In behandeling knop */}
                {punt.status === 'Open' && (
                  <button
                    onClick={() => updatePuntStatus(punt, 'In_Behandeling')}
                    className="text-xs text-orange-600 font-medium flex-shrink-0"
                  >
                    Starten
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}


function PrioriteitBadge({ prioriteit }) {
  const stijl = {
    'Hoog':    'bg-red-100 text-red-700',
    'Normaal': 'bg-gray-100 text-gray-600',
    'Laag':    'bg-green-100 text-green-700',
  }[prioriteit] ?? 'bg-gray-100 text-gray-600'

  return (
    <span className={`text-xs px-1.5 py-0.5 rounded ${stijl}`}>
      {prioriteit}
    </span>
  )
}
